import React from "react";

import { faClose } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { faPlus } from "@awesome.me/kit-4c83d50d0d/icons/classic/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";

import { Button } from "~/ui";
import { IconButton, type IconButtonProps } from "~/ui/button/IconButton";

import styles from "./SidePanelItem.module.css";

type SidePanelItemProps = React.PropsWithChildren<React.HTMLProps<HTMLElement>> & { expandToBottom?: boolean };

export function SidePanelItem({ children, className, expandToBottom, ...rest }: SidePanelItemProps) {
	return (
		<section className={cx(styles.wrapper, { [styles.expandToBottom]: expandToBottom }, className)} {...rest}>
			{children}
		</section>
	);
}

type HeaderProps = React.PropsWithChildren<React.HTMLProps<HTMLHeadElement>> & {
	headerTitle: React.ReactElement<TitleProps>;
	createAction?: () => void;
	actionsMenu?: React.ReactNode;
	onClose?: () => void;
};

/**
 * Header component for the SidePanelItem.
 * Displays a title, optional create action button, optional actions menu, and close button.
 *
 * @param headerTitle - The title element to display in the header (SidePanelItem.Title)
 * @param createAction - Optional callback function to execute when the create button is clicked
 * @param actionsMenu - Optional React node for additional menu actions (Menu)
 * @param children - Optional child elements to render below the header content
 * @param onClose - Callback function to execute when the close button is clicked
 * @param className - Additional CSS class names
 * @param rest - Additional HTML header element attributes
 */
export function Header({ headerTitle, createAction, actionsMenu, children, className, onClose, ...rest }: HeaderProps) {
	return (
		<header className={cx(styles.header, className)} {...rest}>
			<section className={styles.headerContent}>
				{headerTitle}
				{createAction && (
					<Button variant="primary" className={cx(styles.createButton, className)} onClick={createAction}>
						<FontAwesomeIcon className={styles.icon} icon={faPlus} />
					</Button>
				)}
				{actionsMenu && actionsMenu}
				{onClose && <IconButton variant="tertiary" icon={faClose} onClick={onClose} />}
			</section>
			{children}
		</header>
	);
}

Header.displayName = "SidePanelItem.Header";
SidePanelItem.Header = Header;

type TitleProps = React.PropsWithChildren<React.HTMLProps<HTMLHeadingElement>>;

export function Title({ children, className, ...rest }: TitleProps) {
	return (
		<h2 className={cx(styles.title, className)} {...rest}>
			{children}
		</h2>
	);
}

Title.displayName = "SidePanelItem.Title";
SidePanelItem.Title = Title;

export const HeaderIconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(function HeaderIconButton(
	props: IconButtonProps,
	ref,
) {
	return <IconButton ref={ref} variant="tertiary" {...props} />;
});

HeaderIconButton.displayName = "SidePanelItem.HeaderIconButton";
SidePanelItem.HeaderIconButton = HeaderIconButton;

type BodyProps = React.PropsWithChildren<React.HTMLProps<HTMLDivElement>>;

const Body = React.forwardRef<HTMLDivElement, BodyProps>(({ children, className, ...rest }, ref) => {
	return (
		<div ref={ref} className={cx(styles.body, className)} {...rest}>
			{children}
		</div>
	);
});

Body.displayName = "SidePanelItem.Body";
SidePanelItem.Body = Body;
